
import { Model } from '../../model/model';
import { ChronocodeVO } from '../../vo/core/chronocode.vo';
import { ChronocodeEngine as BaseChronocodeEngine, EM_ACTION, VisionVO } from '@wizeoo/akm-compute-lib';
import { BusinessException, ChronoCode, EMPackEntry, EntityModificationPack } from '@wizeoo/akm-message-lib';
import { ChronoCodeLevelVO } from '../../vo/core/chronocodelevel.vo';
import { cloneDeep } from 'lodash-es';
import { WIZ_COMMAND_STATE, WizDateUtils } from '@wizeoo/akm-core-lib';
import { TimeGapVO } from './../../vo/core/timegap.vo';
import { TIME_UNIT } from '../../model/enums';
import { FIELD_KIND } from '@wizeoo/akm-compute-lib';
import { FakeVO } from '../../vo/common/tool/fake.vo';
import { EMEngine } from '../entities/entity-modification.engine';
import { CommitCmd } from '../../command/em/commit.cmd';
import { ViewData } from '../../model/view.data';
import { WizIcon, WizPopUp } from '@wizeoo/akm-component-lib';
import { StaticFeatureEnum } from '../permission/permissions.types';
import { ChronocodeTemplateVO } from '../../vo/core/chronocode-template.vo';

/**
 * Manager de chronocodes pour le moteur
 */
export class ChronocodeEngine extends BaseChronocodeEngine {

    constructor(model: Model) {
        super(model);
    }

    public get model(): Model {
      return this._model as Model;
    }
    public set model(value: Model) {
        this._model = value;
    }

    /**
     * Alimentation des chronocodes du modèle
     * @override pour obtenir les bons types
     * @param entities: ChronoCode[]
     */
    public feedChronocodesMap(entities: ChronoCode[]) {
        const chronocodes: ChronocodeVO[] = entities.map((chronocode) => new ChronocodeVO(this.model, chronocode)); // liste brute
  
        const chronoMap: Map<string, ChronocodeVO> = new Map<string, ChronocodeVO>();
        chronocodes.forEach((chro: ChronocodeVO) => {
          // ajout à la map
          chronoMap.set(chro.code, chro);
        });
  
        this._model.chronocodeList = chronocodes;
        this.setChronocodesMap(chronoMap);
    }

    /**
     * @override pour construction de la treeList
    */
    public setChronocodesMap(value: Map<string, ChronocodeVO>) {
        super.setChronocodesMap(value);
        const chronoTree: ChronocodeVO[] = this.buildChronocodeTree(value);
        (this._model as Model).chronocodeTreeList = chronoTree;
    }

    public getChronocodeList(): ChronocodeVO[] {
        return this._model.chronocodeList ? this._model.chronocodeList as ChronocodeVO[] : null;
    }

    public getChronocodesMap(): Map<string, ChronocodeVO> {
        return this._model.chronocodesMap ? this._model.chronocodesMap as Map<string, ChronocodeVO>: null;
    }

    /**
     * Recherche d'un chronocode dans le model en fonction d'un code (le vrai chronocode)
     * @param pCode Le code du chrono
     */
    public getChronocode(pCode: string): ChronocodeVO {
        return this.getChronocodesMap()?.get(pCode);
    }

    /**
     * Recherche des chronocodes pour un niveau et le code chrono
     * @param pCode Le code du chrono
     */
    public getChronocodesByLevel(pCode: string, pLevel : number): ChronocodeVO[] {
        let result: ChronocodeVO[] = [];
        this.getChronocodeList().forEach(chro => {
            if(chro.rootCode === pCode && chro.level === pLevel) {
                result.push(chro);
            }
        })

        return result;
    }

    /**
     * Recherche d'un chronocodelevel sur le chronocode pour affichage du libellé
     * @param chrono: chronocodeVO
     * @param level: number
     * @return ChronoCodeLevelVO
     */
    public getChronocodeLevel(chrono: ChronocodeVO, level: number): ChronoCodeLevelVO {
        return chrono?.chronoCodeLevelVOs[level];
    }

    /**
     * Construction d'un arbre de chronocodes de niveau 1
     * @param chronoMap: Map<string, ChronocodeVO> la map construite sur retour de find
     * @return ChronocodeVO[] liste de niveaux 1 sous forme d'arbres
     */
    private buildChronocodeTree(chronoMap: Map<string, ChronocodeVO>): ChronocodeVO[] {
        const result: ChronocodeVO[] = [];

        [...chronoMap.values()].forEach((chro: ChronocodeVO) => {
        if (!!chro.parentCode) {
            // ajout dans la pile d'enfants du parent
            if (!chronoMap.has(chro.parentCode)) {
            console.log('missing parent on chronocode', chro);
            } else {
            (chronoMap.get(chro.parentCode) as ChronocodeVO).addChild(chro);
            }
        }
        if (chro.level === 1) {
            // } || chro.level === 0) { // les niveaux 1 et EON
            // isolation des niveaux 1
            result.push(chro);
        }
        });
        return result;
    }

  /**
   * pour un chronocode donné (un vrai, pas un clone), retourne une liste à plat de clone de tous ses enfants (et lui-même)
   * @param pSourceVO  le chrono à "flatter"
   */
  public flatChronoFamily(pSourceVO: ChronocodeVO): ChronocodeVO[] {
    const result: ChronocodeVO[] = [];
    if (pSourceVO) {
      this.recurseFlatChronoFamily(pSourceVO, result);
      // this.clone = result[0]; // mise à jour de l'instance de travail
    } else {
       //TODO TODO voir pourquo this.clone = null;
    }

    return result;
  }
  private recurseFlatChronoFamily(parent: ChronocodeVO, coll: ChronocodeVO[]): void {
    coll.push(this.cloneChronocode(parent)); // on ajoute l'enfant
    if (!!parent.children) {
      // et on boucle sur ses enfants
      parent.children.forEach((child: ChronocodeVO) => {
        this.recurseFlatChronoFamily(child, coll);
      });
    }
  }

  /**
   * Clonage d'un ChronocodeVO
   * @param pSourceVO L'objet source
   */
  public cloneChronocode(pSourceVO: ChronocodeVO): ChronocodeVO {
    let result: ChronocodeVO = null;
    if (!!pSourceVO) {
      result = new ChronocodeVO(this.model, cloneDeep(pSourceVO.source));
     } else {
      result = null;
    }
    return result;
  }


  /**
   * pour un code donné, retourne le chronocode parent de niveau 1 (utilisé dans les visions, les incExc..)
   * @param code du chrono cible
   * @returns son parent de niveau 1
   */
  public getLevelOne(code: string) {
    let result: ChronocodeVO = this.getChronocode(code);
    while (!!result && result.level > 1) {
      result =this.getChronocode(result.parentCode);
    }
    // if (!result) {
    //   console.log('no parent chrono', code, this.getChronocode(code));
    // }
    return result;
  }


  /* ##############################################################################
          utilitaires timeGap
     ############################################################################## */


  /**
   * Recherche d'un timegap dans le model en fonction d'un code
   * @param pCode Le code de la mesure recherchée
   */
  public getTimeGap(pCode: string): TimeGapVO {
    let result: TimeGapVO = null;
    if (this.model.timegapList != null) {
      for (const timegap of this.model.timegapList) {
        if (timegap.code === pCode) {
          result = timegap;
        }
      }
    }
    return result;
  }

  /**
   *
   * @param pTimeGap Recherche de la prochaine date valide pour commencer un gap calendaire
   */
  public getNextValidStartDate(pTimeGap: TimeGapVO): Date {
    let result: Date = new Date();
    if (pTimeGap && pTimeGap.calendar) {
      switch (pTimeGap.unit) {
        case TIME_UNIT.YEAR:
          if (result.getDate() !== 1 || result.getMonth() !== 0) {
            result = new Date(result.getFullYear() + 1, 0, 1);
          }
          break;
        case TIME_UNIT.MONTH:
          if (result.getDate() !== 1) {
            result.setDate(1);
            result = WizDateUtils.add(result, { months: 1 }) as Date;
          }
          break;
        case TIME_UNIT.WEEK:
          result = this.getNextWeekStartDate(result);
          break;
      }
    }
    return result;
  }

  /**
   * Recherche du prochain début de semaine par rapport à une date
   * @param pDate Date de référence
   * @return Date la date trouvée
   */
  public getNextWeekStartDate(pDate: Date): Date {
    const firstDay: number = this.model.businessUnitEngine.currentBU.firstDayOfWeek;
    if (pDate.getDay() !== firstDay) {
      let offset: number = 0;
      if (pDate.getDay() < firstDay) {
        // Dimanche si on cherche un lundi
        offset = 1;
      } else {
        offset = 7 - (pDate.getDay() - firstDay);
      }
      pDate = WizDateUtils.add(pDate, { days: offset }) as Date;
    }
    return pDate;
  }

  /**
   * Calcul d'une date de fin de période à partir d'une date de début et d'un gap
   * @param pStartDate Date de début
   * @param pTimeGap Time gap
   * @return Date la date de fin calculée
   */
  public computeEndDate(pStartDate: Date, pTimeGap: TimeGapVO): Date {
    let result: Date;
    if (pStartDate && pTimeGap) {
      let dateGap: number = 0;
      let monthGap: number = 0;
      let yearGap: number = 0;
      // Détermination du gap à appliquer entre chaque période
      if (pTimeGap.unit === TIME_UNIT.YEAR) {
        yearGap = pTimeGap.value;
      } else if (pTimeGap.unit === TIME_UNIT.MONTH) {
        monthGap = pTimeGap.value;
      } else if (pTimeGap.unit === TIME_UNIT.WEEK) {
        dateGap = pTimeGap.value * 7;
      } else if (pTimeGap.unit === TIME_UNIT.DAY) {
        dateGap = pTimeGap.value;
      }
      result = WizDateUtils.add(pStartDate, { days: dateGap, months: monthGap, years: yearGap }) as Date;
      result = WizDateUtils.add(result, { days: -1 }) as Date; // La veille
    }
    return result;
  }

  /* ################################################
          chronoFieldsMap (vision)
   ################################################ */

   private _chronoFieldsMap: Map<string, FakeVO> = null;
   private buildMap(): void {
       const fields: FakeVO[] = [];
       fields.push(new FakeVO('code', '$.chrono.field.code.$', 1, FIELD_KIND.STRING));
       fields.push(new FakeVO('shortCode', '$.chrono.field.shortcode.$', 2, FIELD_KIND.STRING));
       fields.push(new FakeVO('label', '$.chrono.field.label.$', 3, FIELD_KIND.TRS_LABEL));
       fields.push(new FakeVO('startDate', '$.chrono.field.startdate.$', 4, FIELD_KIND.DATE));
       fields.push(new FakeVO('endDate', '$.chrono.field.enddate.$', 5, FIELD_KIND.DATE));
       this._chronoFieldsMap = new Map(fields.map(f => [f.code, f]));
   }

   public getChronoFields(): FakeVO[] {
       if (!this._chronoFieldsMap) {
           this.buildMap();
       }
       return [...this._chronoFieldsMap.values()];
   }

   public getChronoFieldByCode(code: string): FakeVO {
       if (!this._chronoFieldsMap) {
           this.buildMap();
       }
       return this._chronoFieldsMap.get(code);
   }

  /**
   * retourne tous les enfants du niveau donnée pour un chronocode root
   * @param chronocode la source
   * @param requiredLevel le niveau recherché
   */
  public getChildrenByLevel(chronocode: ChronocodeVO, requiredLevel: number): ChronocodeVO[] {
    const result: ChronocodeVO[] = [];
    this.recurseChildrenByLevel(chronocode, requiredLevel, result);
    return result;
  }
  
  private recurseChildrenByLevel(chronocode: ChronocodeVO, requiredLevel: number, result: ChronocodeVO[]) {
    if (chronocode.level === requiredLevel) {
      result.push(chronocode);
    } else {
      chronocode.children.forEach((child: ChronocodeVO) => {
        this.recurseChildrenByLevel(child, requiredLevel, result);
      });
    }
  }

  /* ################################################
                 persistence
   ################################################ */

  /**
   * create/ update d'une liste de chronocodes
   * @param clones le clone en cours de création, avec ses enfants
   * @param callback retour dans la vue
   */
  public chronocodeMerge(clones: ChronocodeVO[], callback: Function): void {
    let pack: EntityModificationPack;
    for (const clone of clones) {
      const realVO: ChronocodeVO = this.getChronocode(clone.code);
      pack = EMEngine.addMergeEM(clone, realVO, pack);
    }
    if (pack && pack.entries && pack.entries.length > 0) {
      new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), null, callback).execute();
    }
  }

  /**
   * create/ update d'une liste de chronocodes
   * @param clones le clone en cours de création, avec ses enfants
   * @param callback retour dans la vue
   */
  public save(clones: ChronocodeVO[], callback: Function): void {
    let pack: EntityModificationPack;
    for (const clone of clones) {
      pack = EMEngine.addCreationEM(clone, pack);
    }
    new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), clones, callback).execute();
  }

  /**
   * Suppression de chronocode
   * @param chronocode Chronocode à supprimer avec sa descendance
   * @param callback Callback pour le retour de service
   */
  public remove(chronocode: ChronocodeVO, callback: Function): void {
    const pack: EntityModificationPack = this.recurseRemoveChronocode(chronocode, null);
    new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [chronocode], callback).execute();
  }

  /**
   * Suppression récursive de chronocodes
   * @param chronocode Chronocode à supprimer
   * @param pack Pack de suppression
   */
  private recurseRemoveChronocode(
    chronocode: ChronocodeVO,
    pack: EntityModificationPack
  ): EntityModificationPack {
    pack = EMEngine.addRemoveEM(chronocode, pack);
    if (chronocode.children) {
      for (const child of chronocode.children) {
        this.recurseRemoveChronocode(child, pack);
      }
    }
    return pack;
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      let vo: ChronocodeVO = cmd.sourceVO ? (cmd.sourceVO[0] as ChronocodeVO) : null; // uniquement sur create
      // une seule modification à la fois, packEntry donne l'objet
      // const source: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });
      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            // vo.source = source; // le clone
            // const modelChronocode: ChronocodeVO = this.model.chronocodesMap.get(source.code);
            // if (!!modelChronocode) {
            //   modelChronocode.source = source;
            // }

            cmd.result.entries.forEach((ep: EMPackEntry) => {
              // on met à jour les vrais chronos, pas les clones
              const srcToExist: ChronocodeVO = this.model.chronocodeEngine.getChronocode((ep.entity as ChronoCode).code);
              srcToExist.source = ep.entity as ChronoCode;
            });
            break;
          case EM_ACTION.CREATE:
            const toBuildTree: ChronocodeVO[] = [];
            cmd.result.entries.forEach((ep: EMPackEntry) => {
              const srcToExist: ChronocodeVO = new ChronocodeVO(this.model, ep.entity as ChronoCode);
              toBuildTree.push(srcToExist);
              this.model.chronocodeEngine.getChronocodesMap().set(srcToExist.code, srcToExist);
              this.model.chronocodeEngine.getChronocodeList().push(srcToExist);
              // ici, c'est uniquement le niveau 1
              if (srcToExist.level === 1) {
                this.model.chronocodeTreeList.push(srcToExist);
                vo = srcToExist;
              }
            });

            toBuildTree.forEach((chro: ChronocodeVO) => {
              // on alimente les enfants
              this.model.chronocodeEngine.getChronocodesMap().get(chro.parentCode).addChild(chro);
            });
            break;
          case EM_ACTION.DELETE: // en cas de delete, on a supprimé tout une pile. Faire le traitement pour chaque élément
            cmd.result.entries.forEach((ep: EMPackEntry) => {
              const srcToDie: ChronoCode = ep.entity as ChronoCode;
              const realChrono: ChronocodeVO = this.model.chronocodeEngine.getChronocode(srcToDie.code);
              this.model.chronocodeEngine.getChronocodesMap().delete(srcToDie.code);
              this.model.chronocodeEngine.getChronocodeList().splice(this.model.chronocodeEngine.getChronocodeList().indexOf(realChrono), 1);
              // ici, c'est uniquement le niveau 1
              if (srcToDie.level === 1) {
                this.model.chronocodeTreeList.splice(
                  this.model.chronocodeTreeList.indexOf(realChrono),
                  1
                );
                vo = realChrono;
              }
            });
            break;
        }
      }
      cmd.viewCallBack(vo, packStatus); // retour en vue
    }
  }


  /**
   * On flag les chronocodes s'ils sont utilisés par une vision
   * Uniquement les chronos de level 1. Ce sont les seuls utilisables dans une vision
   * @param chronocodeList Liste de chronocodes à vérifier
   */
  public checkUsedChronocodes(chronoTreeList: ChronocodeVO[]): void {
    // on fait la map des chronocodes des visions... et un simple has()
    const visions: VisionVO[] = this.model.visionEngine.getCurrentBuVisions();
    const usedChronoCodes: Map<string, VisionVO> = new Map(visions.map((vis) => [vis.chronoCode, vis]));

    chronoTreeList?.forEach((chro: ChronocodeVO) => {
      if (usedChronoCodes.has(chro.code)) {
        // dans la pile de vision, plus supprimable
        chro.isUsed = true;
      }
    });
  }

  /** Recherche le chrono à sélectionner par défaut, soit le dernier édité, soit celui du contexte... soit rien */
  public getDefaulChronocode(): ChronocodeVO {
    if (this.model.currentChronocode) {
      // dernier role édité
      return this.model.currentChronocode;
    } else {
      // rien d'actif, recherche d'un role du contexte
      const contextChronocode: string = this.model.userContextService.getTab(
        StaticFeatureEnum.CHRONOCODE
      )?.selectedItemId;
      if (contextChronocode) {
        return this.getChronocodesMap().get(contextChronocode);
      }
    }
    return null;
  }

  /**
   * sélection et clone de l'élément de travail (niveau 1 uniquement)
   * @param pChronocode chronocode
   */
  public setCurrentEdited(pChronocode: ChronocodeVO): void {
    this.model.currentChronocode = pChronocode;
    // construction du clone de travail
    this.model.cloneChronocode = this.cloneChronocode(pChronocode);
  }

  /** demande de création */
  public addChronocode(): void {
    const template: ChronocodeTemplateVO = new ChronocodeTemplateVO(this.model);
    // const parentVO: ChronocodeVO = this.getChronocode(ViewData.defaultChronoCode);
    template.domain = this.model.domainCode;
    template.businessUnitCode = this.model.businessUnitCode;
    template.parentCode = ViewData.defaultChronoCode; // parentVO.code;
    template.startDate = WizDateUtils.dateToNumber(WizDateUtils.TODAY);
    template.level = 1;
    template.timeGapCode = TIME_UNIT.UNDEFINED;

    this.model.chronoCodeTemplate = template;
  }

  /**
   * Calcul du nombre de jours, samedis, dimanche d'un chronocode en fonction de ses dates
   * @param pVO Chronocode à alimenter
   */
  public computeChronocodeDays(pChronocodeVO: ChronocodeVO): void {
    if (!!pChronocodeVO && !!pChronocodeVO.startDate && !!pChronocodeVO.endDate) {
      const includedDates: Date[] = WizDateUtils.getDays(pChronocodeVO.startDate, pChronocodeVO.endDate);
      pChronocodeVO.nbDays = includedDates.length;
      pChronocodeVO.nbSaturdays = pChronocodeVO.nbSundays = 0;
      if (includedDates.length > 0) {
        const firstDate: Date = includedDates[0];
        pChronocodeVO.nbSundays = this.getNumberOfDays(firstDate, includedDates.length, 0);
        pChronocodeVO.nbSaturdays = this.getNumberOfDays(firstDate, includedDates.length, 6);
      }
    }
  }

  /**
   * Retourne le nombre d'un certain jour dans une période donnée
   * @param pStartDate Date de début de la période
   * @param pNbDays Nombre de jours de la période
   * @param pDayNum Numéro du jour (dimanche=0, lundi=1, ...)
   * @return Le nombre de jours trouvés
   */
  public getNumberOfDays(pStartDate: Date, pNbDays: number, pDayNum: number): number {
    // Nombre de jour pour atteindre le 1er jour recherché depuis la date de début
    const toNexttargetedDay: number = (7 + pDayNum - pStartDate.getDay()) % 7;
    // Nombre de jour depuis cette 1ere trouvaille pour atteindre la fin de la période
    const daysFromFirstTargetedDay = Math.max(pNbDays - toNexttargetedDay, 0);
    // Calcul du résultat
    return Math.ceil(daysFromFirstTargetedDay / 7);
  }

}
